import PageContainer from '@components/PageContainer';
import AuthFormLayout from '@layouts/form/AuthFormLayout';
import {
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import { AUTH_EVENTS } from '@utils/eventSchema';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMixpanel } from '../../context/MixpanelProvider';
import { useSendOtp } from '../../hooks/postQueries';

const Welcome = () => {
  const { trackEvent } = useMixpanel();
  const navigate = useNavigate();
  const { mutateAsync: sendOtp, isPending } = useSendOtp();
  const { handleSubmit, control } = useForm({
    defaultValues: {
      email: '',
    },
  });

  const handleContinue = async (data) => {
    trackEvent('auth', {
      ...AUTH_EVENTS.SIGNUP_STARTED,
      login_method: 'email',
      source: 'signup_page',
      device: navigator.userAgent,
      email: data.email,
    });

    await sendOtp(
      {
        email: data.email,
        purpose: 'register_user',
      },
      {
        onSuccess: () => {
          navigate('/signup/verify-otp', {
            state: { email: data.email, purpose: 'register_user' },
          });
        },
        onError: (error) => {
          toast.error(error?.response?.data?.message || 'Failed to send OTP. Please try again.');
        },
      },
    );
  };

  return (
    <PageContainer title="Qualz.ai | Signup">
      <AuthFormLayout
        title="Welcome onboard 👋"
        text="Create your account! Join Qualz.ai to streamline your qualitative research journey."
      >
        <form onSubmit={handleSubmit(handleContinue)}>
          <FormControl fullWidth>
            <FormLabel htmlFor="email" sx={{ mb: 1, fontWeight: '500' }}>
              Email *
            </FormLabel>

            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email format',
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <TextField
                    {...field}
                    fullWidth
                    type="email"
                    id="email"
                    data-testid="email-input"
                    placeholder="name@example.com"
                    variant="outlined"
                    error={!!error}
                    helperText={error?.message}
                  />
                </>
              )}
            />
          </FormControl>
          <Button
            fullWidth
            variant="contained"
            data-testid="continue-button"
            size="large"
            type="submit"
            sx={{ my: 2 }}
            aria-live="polite"
          >
            {isPending ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
          </Button>
        </form>

        <Typography variant="body2" align="center" sx={{ mt: 1, mb: 3 }}>
          By clicking &ldquo;Continue&rdquo; you agree to our{' '}
          <Link
            to="https://qualzwebsitestatic.blob.core.windows.net/qualzai/qualzai%20inc.%20-%20Website%20Terms%20of%20Use.pdf"
            underline="hover"
            target="_blank"
          >
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link
            to="https://qualzwebsitestatic.blob.core.windows.net/qualzai/qualzai%20inc.%20-%20Privacy_Policy.pdf"
            underline="hover"
            target="_blank"
          >
            Privacy Policy
          </Link>
        </Typography>
        <Typography variant="body1" align="center" sx={{ mt: 1 }}>
          Already have an account?{' '}
          <Link to="/signin" underline="hover">
            Sign in
          </Link>
        </Typography>
      </AuthFormLayout>
    </PageContainer>
  );
};

export default Welcome;
